import { Dispatch, SetStateAction } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { MusicGenre } from '../../../#interfaces/interfaces';
import {
    Box, Chip, Dialog, DialogActions, DialogContent,
    DialogTitle, Divider, IconButton, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import AddIcon from '@mui/icons-material/Add';

const BG_SECTION     = '#1c1c24';
const BRAND_RED      = '#c0392b';
const TEXT_PRIMARY   = '#f4f4f6';
const TEXT_SECONDARY = 'rgba(244,244,246,0.65)';
const BORDER_SUBTLE  = '1px solid rgba(255,255,255,0.08)';
const MAX_GENRES     = 20;

interface ModalSelectGroupGenresProps {
    setSelectedGenres: Dispatch<SetStateAction<number[]>>;
    selectedGenres: number[];
    close: Dispatch<SetStateAction<boolean>>;
}

export function ModalSelectGroupGenres({ selectedGenres, setSelectedGenres, close }: ModalSelectGroupGenresProps) {
    const allMusicalGenres = useSelector((state: RootState) => state.sessionGroups.musicalGenres);
    const limitReached = selectedGenres.length >= MAX_GENRES;

    function handleGenreSelection(genreId: number) {
        if (selectedGenres.includes(genreId)) {
            setSelectedGenres(selectedGenres.filter(id => id !== genreId));
        } else if (!limitReached) {
            setSelectedGenres([...selectedGenres, genreId]);
        }
    }

    return (
        <Dialog
            open
            maxWidth='sm'
            fullWidth
            onClose={() => close(false)}
            PaperProps={{ sx: { bgcolor: BG_SECTION, border: BORDER_SUBTLE, borderRadius: '12px' } }}
        >
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
                <Box>
                    <Typography variant='h6' fontWeight={700} color={TEXT_PRIMARY}>Select Group Genres</Typography>
                    <Typography variant='caption' color={TEXT_SECONDARY}>
                        {selectedGenres.length}/{MAX_GENRES} selected
                    </Typography>
                </Box>
                <IconButton onClick={() => close(false)} size='small' sx={{ color: TEXT_SECONDARY }}>
                    <CloseIcon fontSize='small' />
                </IconButton>
            </DialogTitle>

            <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)' }} />

            <DialogContent sx={{ pt: 2 }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {allMusicalGenres.map((item: MusicGenre, index: number) => {
                        const selected  = selectedGenres.includes(item.id);
                        const disabled  = limitReached && !selected;
                        return (
                            <Chip
                                key={index}
                                label={item.description}
                                size='small'
                                icon={selected
                                    ? <CheckIcon sx={{ fontSize: '13px !important', color: '#fff !important' }} />
                                    : <AddIcon sx={{ fontSize: '13px !important' }} />}
                                onClick={() => handleGenreSelection(item.id)}
                                disabled={disabled}
                                sx={{
                                    bgcolor: selected ? BRAND_RED : 'rgba(255,255,255,0.07)',
                                    color: selected ? '#fff' : TEXT_PRIMARY,
                                    cursor: disabled ? 'not-allowed' : 'pointer',
                                    fontWeight: selected ? 600 : 400,
                                    '&:hover': { bgcolor: selected ? '#a93226' : 'rgba(255,255,255,0.12)' },
                                    '&.Mui-disabled': { opacity: 0.4 },
                                    transition: 'background-color 0.15s',
                                }}
                            />
                        );
                    })}
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2.5 }}>
                <Typography variant='caption' color={limitReached ? '#e57373' : TEXT_SECONDARY} sx={{ flex: 1 }}>
                    {limitReached ? `Limit of ${MAX_GENRES} genres reached` : ''}
                </Typography>
            </DialogActions>
        </Dialog>
    );
}
